"use server";

import { takenSlots } from "./taken-slots";
import { z } from "zod";
import { defineAction } from "@/lib/define-action";
import { prisma } from "@/lib/prisma";
import { queueTimeEntrySync } from "@/lib/bexio/queue";
import { CACHE_TAGS } from "@/lib/cache";
import { computeStartAndEndTimes, shiftPastOverlaps } from "./time-calc";
import { can } from "@/lib/access-rules";
import type { TimeTemplateItem } from "./types";
import { entryProjectId } from "@/lib/projects/entry-project";

/**
 * Fetch all personal templates belonging to the current user (SPEC §4.3, D37).
 * Per-user only — never returns other users' templates.
 */
export const getTimeTemplatesAction = defineAction({
  guard: "time:log",
  run: async ({ userId }): Promise<TimeTemplateItem[]> => {
    const [templates, activities] = await Promise.all([
      prisma.timeTemplate.findMany({
        where: { userId },
        include: {
          workPackage: {
            include: {
              bexioProject: {
                include: {
                  client: { select: { id: true, name: true, logoUrl: true } },
                },
              },
            },
          },
        },
        orderBy: { name: "asc" },
      }),
      prisma.businessActivity.findMany({
        select: { id: true, name: true },
      }),
    ]);

    const activityMap = new Map(activities.map((a) => [a.id, a.name]));

    return templates.map((t) => ({
      id: t.id,
      name: t.name,
      workPackageId: t.workPackageId,
      workPackageName: t.workPackage?.name ?? null,
      bexioProjectId: t.workPackage?.bexioProject.id ?? null,
      bexioProjectName: t.workPackage?.bexioProject.name ?? null,
      clientId: t.workPackage?.bexioProject.client?.id ?? null,
      clientName:
        t.workPackage?.bexioProject.client?.name ??
        t.workPackage?.bexioProject.name ??
        null,
      clientLogoUrl: t.workPackage?.bexioProject.client?.logoUrl ?? null,
      activityId: t.activityId,
      activityName: t.activityId ? (activityMap.get(t.activityId) ?? null) : null,
      billable: t.billable,
      minutes: t.minutes,
      note: t.note,
      createdAt: t.createdAt.toISOString(),
      updatedAt: t.updatedAt.toISOString(),
    }));
  },
});

const createTimeTemplateSchema = z.object({
  name: z.string().trim().min(1, "Template name is required").max(255),
  workPackageId: z.number().int().positive().optional().nullable(),
  activityId: z.number().int().positive().optional().nullable(),
  billable: z.boolean().optional().default(true),
  minutes: z.number().int().positive().optional().nullable(),
  note: z.string().optional().nullable(),
});

/**
 * Create a new personal time template (SPEC §4.3, D37).
 * Stores project/package, activity, billable, duration, and note text.
 */
export const createTimeTemplateAction = defineAction({
  guard: "time:log",
  input: createTimeTemplateSchema,
  run: async ({ userId }, input: z.input<typeof createTimeTemplateSchema>) => {
    // If workPackageId provided, verify package exists and is not archived
    if (input.workPackageId) {
      const pkg = await prisma.workPackage.findUnique({
        where: { id: input.workPackageId },
        select: { id: true, archivedAt: true },
      });
      if (!pkg) {
        throw new Error("Budget not found");
      }
      if (pkg.archivedAt) {
        throw new Error("Cannot associate template with an archived budget");
      }
    }

    const template = await prisma.timeTemplate.create({
      data: {
        userId,
        name: input.name.trim(),
        workPackageId: input.workPackageId ?? null,
        activityId: input.activityId ?? null,
        billable: input.billable ?? true,
        minutes: input.minutes ?? null,
        note: input.note ? input.note.trim() : null,
      },
      include: {
        workPackage: {
          include: {
            bexioProject: {
              include: {
                client: { select: { id: true, name: true, logoUrl: true } },
              },
            },
          },
        },
      },
    });

    return {
      ok: true as const,
      template: {
        id: template.id,
        name: template.name,
        workPackageId: template.workPackageId,
        workPackageName: template.workPackage?.name ?? null,
        bexioProjectId: template.workPackage?.bexioProject.id ?? null,
        bexioProjectName: template.workPackage?.bexioProject.name ?? null,
        clientId: template.workPackage?.bexioProject.client?.id ?? null,
        clientName:
          template.workPackage?.bexioProject.client?.name ??
          template.workPackage?.bexioProject.name ??
          null,
        clientLogoUrl: template.workPackage?.bexioProject.client?.logoUrl ?? null,
        activityId: template.activityId,
        billable: template.billable,
        minutes: template.minutes,
        note: template.note,
        createdAt: template.createdAt.toISOString(),
        updatedAt: template.updatedAt.toISOString(),
      },
    };
  },
  tag: CACHE_TAGS.settings,
  paths: ["/bexio"],
});

const updateTimeTemplateSchema = z.object({
  id: z.number().int().positive("Template ID is required"),
  name: z.string().trim().min(1, "Template name is required").max(255),
  workPackageId: z.number().int().positive().optional().nullable(),
  activityId: z.number().int().positive().optional().nullable(),
  billable: z.boolean().optional(),
  minutes: z.number().int().positive().optional().nullable(),
  note: z.string().optional().nullable(),
});

/**
 * Edit an existing personal time template (SPEC §4.3, D37).
 * Ensures ownership — users can only edit their own templates.
 */
export const updateTimeTemplateAction = defineAction({
  guard: "time:log",
  input: updateTimeTemplateSchema,
  run: async ({ userId }, input: z.input<typeof updateTimeTemplateSchema>) => {
    // Check ownership
    const existing = await prisma.timeTemplate.findUnique({
      where: { id: input.id },
    });
    if (!existing || existing.userId !== userId) {
      throw new Error("Template not found or access denied");
    }

    if (input.workPackageId) {
      const pkg = await prisma.workPackage.findUnique({
        where: { id: input.workPackageId },
        select: { id: true, archivedAt: true },
      });
      if (!pkg) {
        throw new Error("Budget not found");
      }
      if (pkg.archivedAt) {
        throw new Error("Cannot associate template with an archived budget");
      }
    }

    const updated = await prisma.timeTemplate.update({
      where: { id: input.id },
      data: {
        name: input.name.trim(),
        workPackageId:
          input.workPackageId === undefined ? existing.workPackageId : input.workPackageId,
        activityId:
          input.activityId === undefined ? existing.activityId : input.activityId,
        billable:
          input.billable === undefined ? existing.billable : input.billable,
        minutes:
          input.minutes === undefined ? existing.minutes : input.minutes,
        note:
          input.note === undefined
            ? existing.note
            : input.note
            ? input.note.trim()
            : null,
      },
      include: {
        workPackage: {
          include: {
            bexioProject: {
              include: {
                client: { select: { id: true, name: true, logoUrl: true } },
              },
            },
          },
        },
      },
    });

    return {
      ok: true as const,
      template: {
        id: updated.id,
        name: updated.name,
        workPackageId: updated.workPackageId,
        workPackageName: updated.workPackage?.name ?? null,
        bexioProjectId: updated.workPackage?.bexioProject.id ?? null,
        bexioProjectName: updated.workPackage?.bexioProject.name ?? null,
        clientId: updated.workPackage?.bexioProject.client?.id ?? null,
        clientName:
          updated.workPackage?.bexioProject.client?.name ??
          updated.workPackage?.bexioProject.name ??
          null,
        clientLogoUrl: updated.workPackage?.bexioProject.client?.logoUrl ?? null,
        activityId: updated.activityId,
        billable: updated.billable,
        minutes: updated.minutes,
        note: updated.note,
        createdAt: updated.createdAt.toISOString(),
        updatedAt: updated.updatedAt.toISOString(),
      },
    };
  },
  tag: CACHE_TAGS.settings,
  paths: ["/bexio"],
});

const deleteTimeTemplateSchema = z.object({
  id: z.number().int().positive("Template ID is required"),
});

/**
 * Delete a personal time template (SPEC §4.3, D37).
 * Ensures ownership — users can only delete their own templates.
 */
export const deleteTimeTemplateAction = defineAction({
  guard: "time:log",
  input: deleteTimeTemplateSchema,
  run: async ({ userId }, input: z.input<typeof deleteTimeTemplateSchema>) => {
    const existing = await prisma.timeTemplate.findUnique({
      where: { id: input.id },
    });
    if (!existing || existing.userId !== userId) {
      throw new Error("Template not found or access denied");
    }

    await prisma.timeTemplate.delete({
      where: { id: input.id },
    });

    return { ok: true as const, id: input.id };
  },
  tag: CACHE_TAGS.settings,
  paths: ["/bexio"],
});

const applyTemplateSchema = z.object({
  templateId: z.number().int().positive("Template ID is required"),
  date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, "Invalid date format"),
  workPackageId: z.number().int().positive().optional(),
  customStartTime: z.string().regex(/^\d{2}:\d{2}$/).optional(),
  customEndTime: z.string().regex(/^\d{2}:\d{2}$/).optional(),
  overrideMinutes: z.number().int().positive().optional(),
  overrideNote: z.string().optional(),
});

/**
 * Apply a personal time template to log time directly (SPEC §4.3, D37).
 * One-click save directly from the week grid or form.
 */
export const applyTemplateAndLogTimeAction = defineAction({
  guard: "time:log",
  input: applyTemplateSchema,
  run: async ({ userId, access }, input: z.input<typeof applyTemplateSchema>) => {
    // 1. SPEC §3.4 & C6: Unmapped users stopped at save with exact message.
    const mapping = await prisma.bexioUserMapping.findUnique({
      where: { userId },
    });
    if (!mapping) {
      throw new Error(
        "Your Hub account isn't linked to Bexio yet. Ask your manager to link your account or give you Bexio access."
      );
    }

    // 2. Load personal template belonging to current user
    const template = await prisma.timeTemplate.findUnique({
      where: { id: input.templateId },
      include: {
        workPackage: {
          include: {
            bexioProject: true,
          },
        },
      },
    });

    if (!template || template.userId !== userId) {
      throw new Error("Template not found or access denied");
    }

    const targetWorkPackageId = input.workPackageId || template.workPackageId;
    if (!targetWorkPackageId) {
      throw new Error(
        "Budget is required. No time can be entered without a budget."
      );
    }

    // Verify package is active
    const pkg =
      targetWorkPackageId === template.workPackageId
        ? template.workPackage
        : await prisma.workPackage.findUnique({
            where: { id: targetWorkPackageId },
            include: { bexioProject: true },
          });

    if (!pkg) {
      throw new Error("Budget not found");
    }
    if (pkg.archivedAt) {
      throw new Error("Cannot book time to a closed budget");
    }

    // 3. Billable permission check (SPEC §4.2, D56)
    const isPm =
      access.roleIsAdmin ||
      can(access, "pm:settings_manage") ||
      can(access, "projects:manage");

    const billable = isPm ? template.billable : true;

    // 4. Compute times & minutes
    const minutes = input.overrideMinutes || template.minutes || 60;
    const note =
      (input.overrideNote && input.overrideNote.trim()) ||
      (template.note && template.note.trim()) ||
      template.name;

    const dateObj = new Date(input.date + "T00:00:00.000Z");

    const times = shiftPastOverlaps(computeStartAndEndTimes({
      minutes,
      customStartTime: input.customStartTime,
      customEndTime: input.customEndTime,
    }), await takenSlots(userId, dateObj));

    const [sh, sm] = times.startTime.split(":").map(Number);
    const startDateTime = new Date(dateObj);
    startDateTime.setUTCHours(sh, sm, 0, 0);

    const [eh, em] = times.endTime.split(":").map(Number);
    const endDateTime = new Date(dateObj);
    endDateTime.setUTCHours(eh, em, 0, 0);

    const hubProjectId = await entryProjectId({ workPackageId: targetWorkPackageId });

    const entry = await prisma.timeEntry.create({
      data: {
        userId,
        date: dateObj,
        minutes,
        workPackageId: targetWorkPackageId,
        activityId: template.activityId,
        billable,
        note,
        startTime: startDateTime,
        endTime: endDateTime,
        hubProjectId,
        source: "HUB",
        syncStatus: "QUEUED",
      },
    });

    // Record initial history (SPEC §4.4, §13)
    await prisma.timeEntryHistory.create({
      data: {
        timeEntryId: entry.id,
        changedBy: userId,
        field: "created",
        newValue: `${minutes} min on package #${targetWorkPackageId} via template "${template.name}"`,
        editNote: `Logged via personal template "${template.name}"`,
      },
    });

    // Queue background sync to Bexio (SPEC §3.3, D31)
    await queueTimeEntrySync(entry.id);

    return { ok: true as const, entryId: entry.id };
  },
  tag: CACHE_TAGS.settings,
  paths: ["/bexio"],
});
